/**
 * Enterprise Help System - Utility Functions
 *
 * Helper functions for creating, registering, and managing help content.
 * These utilities make it easy for any developer to add new help content.
 */

import type {
  HelpContent,
  HelpSection,
  HelpFAQ,
  HelpTip,
  HelpWalkthrough,
  HelpVideo,
  HelpTroubleshooting,
  HelpRelatedLink,
  FieldHelp,
  UserRole,
} from './types';

// ============================================================================
// CONTENT CREATION HELPERS
// ============================================================================

/**
 * Create a help content object with sensible defaults
 *
 * @example
 * ```ts
 * const myPageHelp = createHelpContent({
 *   id: 'my-page',
 *   title: 'My Page',
 *   description: 'Help for my page',
 *   sections: [
 *     createSection('overview', 'Overview', '<p>This is the overview...</p>')
 *   ],
 *   faqs: [
 *     createFAQ('faq-1', 'How do I...?', 'You can...', ['tag1', 'tag2'])
 *   ]
 * });
 * ```
 */
export function createHelpContent(
  content: Partial<HelpContent> & Pick<HelpContent, 'id' | 'title' | 'description'>
): HelpContent {
  const today = new Date().toISOString().split('T')[0];

  return {
    lastUpdated: today,
    sections: [],
    faqs: [],
    relatedLinks: [],
    keywords: generateKeywords(content.title, content.sections),
    ...content,
  };
}

/**
 * Create a help section with optional subsections
 */
export function createSection(
  id: string,
  title: string,
  content: string,
  options?: {
    icon?: string;
    subsections?: HelpSection[];
    tips?: HelpTip[];
    faqs?: HelpFAQ[];
  }
): HelpSection {
  return {
    id,
    title,
    content,
    ...options,
  };
}

/**
 * Create a FAQ item
 */
export function createFAQ(
  id: string,
  question: string,
  answer: string,
  tags: string[] = [],
  options?: { relatedFaqs?: string[]; visibility?: { visibleTo: UserRole[] } }
): HelpFAQ {
  return {
    id,
    question,
    answer,
    tags,
    ...options,
  };
}

/**
 * Create a quick tip
 */
export function createTip(
  id: string,
  type: HelpTip['type'],
  title: string,
  content: string
): HelpTip {
  return { id, type, title, content };
}

/**
 * Create a step-by-step walkthrough
 */
export function createWalkthrough(
  id: string,
  title: string,
  description: string,
  steps: Array<{ title: string; description: string; imageUrl?: string }>,
  estimatedMinutes?: number
): HelpWalkthrough {
  return {
    id,
    title,
    description,
    steps: steps.map((step, index) => ({
      stepNumber: index + 1,
      ...step,
    })),
    estimatedMinutes,
  };
}

/**
 * Create a video tutorial reference
 */
export function createVideo(
  id: string,
  title: string,
  description: string,
  videoUrl: string,
  options?: {
    thumbnailUrl?: string;
    durationSeconds?: number;
    transcript?: string;
  }
): HelpVideo {
  return {
    id,
    title,
    description,
    videoUrl,
    ...options,
  };
}

/**
 * Create a troubleshooting item
 */
export function createTroubleshooting(
  id: string,
  problem: string,
  possibleCauses: string[],
  solutions: Array<{ description: string; steps?: string[] }>,
  supportLink?: string
): HelpTroubleshooting {
  return {
    id,
    problem,
    possibleCauses,
    solutions,
    supportLink,
  };
}

/**
 * Create a related link
 */
export function createRelatedLink(
  title: string,
  href: string,
  options?: {
    description?: string;
    type?: HelpRelatedLink['type'];
    external?: boolean;
  }
): HelpRelatedLink {
  return {
    title,
    href,
    type: options?.type || (href.startsWith('http') ? 'external' : 'internal'),
    ...options,
  };
}

/**
 * Create field-level help for tooltips/popovers
 */
export function createFieldHelp(
  id: string,
  label: string,
  description: string,
  options?: {
    placeholder?: string;
    validation?: string;
    example?: string;
    learnMoreLink?: string;
  }
): FieldHelp {
  return {
    id,
    label,
    description,
    ...options,
  };
}

// ============================================================================
// KEYWORD GENERATION
// ============================================================================

/**
 * Auto-generate keywords from title and sections
 */
export function generateKeywords(
  title: string,
  sections?: HelpSection[]
): string[] {
  const words = new Set<string>();

  // Extract words from title
  title
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .forEach((w) => words.add(w));

  // Extract words from section titles
  sections?.forEach((section) => {
    section.title
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 2)
      .forEach((w) => words.add(w));
  });

  return Array.from(words);
}

// ============================================================================
// CONTENT ID UTILITIES
// ============================================================================

/**
 * Parse a content ID into its parts
 *
 * @example
 * parseContentId('training:library:filters')
 * // Returns: { page: 'training', section: 'library', subsection: 'filters' }
 */
export function parseContentId(contentId: string): {
  page: string;
  section?: string;
  subsection?: string;
  field?: string;
} {
  const parts = contentId.split(':');
  return {
    page: parts[0],
    section: parts[1],
    subsection: parts[2],
    field: parts[3],
  };
}

/**
 * Build a content ID from parts
 *
 * @example
 * buildContentId('training', 'library', 'filters')
 * // Returns: 'training:library:filters'
 */
export function buildContentId(
  page: string,
  section?: string,
  subsection?: string,
  field?: string
): string {
  return [page, section, subsection, field].filter(Boolean).join(':');
}

/**
 * Get the parent content ID
 *
 * @example
 * getParentContentId('training:library:filters')
 * // Returns: 'training:library'
 */
export function getParentContentId(contentId: string): string | null {
  const parts = contentId.split(':');
  if (parts.length <= 1) return null;
  return parts.slice(0, -1).join(':');
}

/**
 * Check if a content ID is a child of another
 */
export function isChildOf(childId: string, parentId: string): boolean {
  return childId.startsWith(parentId + ':');
}

// ============================================================================
// SEARCH UTILITIES
// ============================================================================

/**
 * Search within help content
 */
export function searchHelpContent(
  content: HelpContent,
  query: string
): {
  sections: HelpSection[];
  faqs: HelpFAQ[];
  hasResults: boolean;
} {
  const lowerQuery = query.toLowerCase().trim();

  if (!lowerQuery) {
    return {
      sections: content.sections,
      faqs: content.faqs,
      hasResults: true,
    };
  }

  const matchedSections = content.sections.filter(
    (section) =>
      section.title.toLowerCase().includes(lowerQuery) ||
      section.content.toLowerCase().includes(lowerQuery)
  );

  const matchedFaqs = content.faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(lowerQuery) ||
      faq.answer.toLowerCase().includes(lowerQuery) ||
      faq.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );

  return {
    sections: matchedSections,
    faqs: matchedFaqs,
    hasResults: matchedSections.length > 0 || matchedFaqs.length > 0,
  };
}

// ============================================================================
// ROLE-BASED FILTERING
// ============================================================================

/**
 * Filter content based on user role
 */
export function filterByRole<T extends { visibility?: { visibleTo: UserRole[] } }>(
  items: T[],
  userRole: UserRole
): T[] {
  return items.filter((item) => {
    if (!item.visibility) return true;
    return (
      item.visibility.visibleTo.includes('all') ||
      item.visibility.visibleTo.includes(userRole)
    );
  });
}

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

/**
 * Format duration in seconds to human readable string
 */
export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format date string to human readable
 */
export function formatLastUpdated(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return date.toLocaleDateString();
}
